import React from 'react';
import PropTypes from 'prop-types';
import {Field} from 'redux-form';
import {withApollo} from '@apollo/client/react/hoc';
import {isEmpty as _isEmpty, orderBy} from 'lodash';
import {connect} from 'react-redux';

import {FiltersWrapper} from '@appComponents/HOCFiltersWrapper';
import Form from '@appComponents/ReduxFormControls';
import {EVENT_TYPE_SPORTRADAR, EVENT_TYPE_NON_SPORTRADAR, EVENT_TYPE_STAGE} from '@constants/variables';
import {convertToInt} from '@utils/helpers';
import {sortByKey} from '@utils/sorters';
import mapModulesToProps from '@utils/mapModulesToProps';
import {mapPropertyLicencesForDropdown} from '@utils/propertyLicence';
import {setTvChannelState} from '@modules/events/utils/eventContentHelper';
import {cancelGraphqlDebouncedFetches, createGraphqlDebouncedFetch} from '@utils/graphqlDebounce';
import {filteredSelectedTournament, filteredSelectedTournamentCategory} from '@modules/events/utils/eventSchedule';
import {ContentTypesFilter} from '@utils/commonFilters/ContentTypesFilter';
import {ContentVariantsFilter} from '@utils/commonFilters/ContentVariantsFilter';
import {PropertyLicencesFilter} from '@utils/commonFilters/PropertyLicencesFilter';
import {EventTypeFilter} from '@utils/commonFilters/EventTypeFilter';
import {ProductsFilter} from '@utils/commonFilters/ProductsFilter';
import {SearchFilter} from '@utils/commonFilters/SearchFilter';
import {DistributionTypesFilter} from '@utils/commonFilters/DistributionTypesFilter';
import {ContentCategoriesLevel1Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel1Filter';
import {ContentCategoriesLevel2Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel2Filter';
import {ContentCategoriesLevel3Filter} from '@utils/commonFilters/contentCategories/ContentCategoriesLevel3Filter';
import {SportsFilter} from '@utils/commonFilters/SportsFilter';
import {StageSportsFilter} from '@utils/commonFilters/StageSportsFilter';
import {TournamentsFilter} from '@utils/commonFilters/TournamentsFilter';
import {TournamentCategoriesFilter} from '@utils/commonFilters/TournamentCategoriesFilter';
import {FirstStageCategoriesFilter} from '@utils/commonFilters/FirstStageCategoriesFilter';
import {SecondStageCategoriesFilter} from '@utils/commonFilters/SecondStageCategoryFiter';
import {createCascadeFetchers} from '@utils/commonFilters/cascadeFetchers';
import {getBufferDropdownOptions} from '@modules/liveControl/utils/helpers';

const MESSAGE_BOX_NAME = 'liveControl';

export class LiveControlFilters extends React.Component
{
    static propTypes = {
        client: PropTypes.object,
        data: PropTypes.shape({
            broadcastStatuses: PropTypes.array,
            distributionTypes: PropTypes.array,
            encodingDatacenters: PropTypes.array,
            encodingStatuses: PropTypes.array,
            eventTypes: PropTypes.array,
            products: PropTypes.array,
            propertyLicencesForDropdown: PropTypes.array,
            refetchLiveControl: PropTypes.func,
            setPollingInterval: PropTypes.func,
            tvChannels: PropTypes.array,
            eventContentTypes: PropTypes.array,
            eventContentVariants: PropTypes.array,
        }),
        filters: PropTypes.object,
        form: PropTypes.object,
        loading: PropTypes.bool,
        restoreFilterValues: PropTypes.func.isRequired,
    };

    static defaultProps = {
        data: {
            broadcastStatuses: [],
            distributionTypes: [],
            encodingDatacenters: [],
            encodingStatuses: [],
            eventTypes: [],
            propertyLicencesForDropdown: [],
            products: [],
            eventContentTypes: [],
            eventContentVariants: [],
        },
    };

    constructor() {
        super();

        this.state = {
            isSportradarEvent: false,
            isStageEvent: false,
            isNonSportradarEvent: false,
            hasSelectedSport: false,
            hasSportsWithTvChannel: false,
            hasSelectedCategoryLev1: false,
            hasSelectedCategoryLev2: false,
            hasSelectedTournamentCategory: false,
            hasSelectedTournaments: false,
            tournamentCategories: [],
            tournaments: [],
            courts: [],
            sports: [],
            fetchedSports: [],
            sportsLoading: false,
            tvChannels: [],
            contentCategoriesSecondLevel: [],
            contentCategoriesThirdLevel: [],
            contentCategoriesFirstLevel: [],
            tvChannelsLoading: false,
            contentCategoriesFirstLevelLoading: false,
            contentCategoriesSecondLevelLoading: false,
            contentCategoriesThirdLevelLoading: false,
            tournamentCategoriesLoading: false,
            tournamentsLoading: false,
            courtsLoading: false,
            init: true,
            fetchedStandardSports: [],
            fetchedStageSports: [],
            stageSportsLoading: false,
            firstStageCategories: [],
            firstStageCategoriesLoading: false,
            selectedFirstStageCategories: [],
            secondStageCategories: [],
            secondStageCategoriesLoading: false,
            selectedSecondStageCategories: [],
        };
    }

    componentDidUpdate(prevProps, prevState) {
        let state = {};

        // Restore filters chain from app.filters
        if (this.props.filters
            && this.props.filters.LiveControl
            && this.props.form
            && this.state.init) {
            const filters = this.props.filters.LiveControl;

            // Sportradar type
            let reduxSports = this.props.restoreFilterValues(filters, 'sports');

            if (!_isEmpty(reduxSports)) {
                state.hasSelectedSport = true;

                if (_isEmpty(this.state.tournamentCategories) && this.state.isSportradarEvent) {
                    const sportIds = reduxSports.map(Number);

                    setTvChannelState(this.props.form, sportIds, this.setStateHandler, MESSAGE_BOX_NAME);
                    this.fetchSportradarSportDependenciesDebounced(sportIds);

                    let reduxTournamentCategories = this.props.restoreFilterValues(filters, 'tournamentCategories');

                    if (!_isEmpty(reduxTournamentCategories)) {
                        state.hasSelectedTournamentCategories = true;

                        if (_isEmpty(this.state.tournaments)) {
                            this.fetchTournamentsDebounced(
                                reduxTournamentCategories.map((tournamentCategory) => (
                                    convertToInt(tournamentCategory)
                                ))
                            );

                            let reduxTournaments = this.props.restoreFilterValues(filters, 'tournaments');

                            if (!_isEmpty(reduxTournaments)) {
                                state.selectedTournaments = reduxTournaments;
                            }
                        }
                    }
                }
            }

            // Non Sportradar
            let reduxContentCategoriesLvl1 = this.props.restoreFilterValues(filters, 'contentCategoriesLevel1');

            if (!_isEmpty(reduxContentCategoriesLvl1)) {
                state.hasSelectedCategoryLev1 = true;

                if (_isEmpty(this.state.contentCategoriesSecondLevel)) {
                    this.getSecondLevelContentCategoriesFromGraphQL(reduxContentCategoriesLvl1);

                    let reduxContentCategoriesLvl2 = this.props.restoreFilterValues(filters, 'contentCategoriesLevel2');

                    if (!_isEmpty(reduxContentCategoriesLvl2)) {
                        state.hasSelectedCategoryLev2 = true;

                        if (_isEmpty(this.state.contentCategoriesThirdLevel)) {
                            this.getThirdLevelContentCategoriesFromGraphQL(reduxContentCategoriesLvl2);

                            let reduxContentCategoriesLvl3 = this.props.restoreFilterValues(filters, 'contentCategoriesLevel3');

                            if (!_isEmpty(reduxContentCategoriesLvl3)) {
                                state.selectedThirdLevelCategories = reduxContentCategoriesLvl3;
                            }
                        }
                    }
                }
            }
        }

        if (!_isEmpty(state)) {
            state.init = false;
            this.setState(() => (state));
        }

        filteredSelectedTournamentCategory(this.state.selectedSports, prevState, this.setState.bind(this));
        filteredSelectedTournament(
            prevState,
            this.setState.bind(this),
            this.state.tournaments,
            this.state.selectedTournaments
        );
    }

    componentWillUnmount() {
        this.cancelDebouncedFetches();
    }

    cancelDebouncedFetches = () => {
        cancelGraphqlDebouncedFetches([
            this.fetchSportradarSportDependenciesDebounced,
            this.fetchTournamentsDebounced,
            this.fetchCourtsDebounced,
            this.fetchFirstStageCategoriesDebounced,
            this.fetchSecondStageCategoriesDebounced,
            this.getSecondLevelContentCategoriesFromGraphQL,
            this.getThirdLevelContentCategoriesFromGraphQL,
        ]);
    };

    onEventTypeChange = (event, value) => {
        const eventType = convertToInt(value);
        const isSportradarEvent = eventType === EVENT_TYPE_SPORTRADAR;
        const isStageEvent = eventType === EVENT_TYPE_STAGE;
        const isNonSportradarEvent = eventType === EVENT_TYPE_NON_SPORTRADAR;

        this.cancelDebouncedFetches();

        let state = {
            hasSelectedSport: false,
            hasSelectedTournamentCategory: false,
            hasSelectedCategoryLev1: false,
            hasSelectedCategoryLev2: false,
            isSportradarEvent: isSportradarEvent,
            isStageEvent: isStageEvent,
            isNonSportradarEvent: isNonSportradarEvent,
        };

        if (isSportradarEvent) {
            this.props.form.changeValue('contentCategoriesLevel1', null);
            this.props.form.changeValue('contentCategoriesLevel2', null);
            this.props.form.changeValue('contentCategoriesLevel3', null);
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);
            this.props.form.changeValue('sports', null);
            state.selectedFirstLevelCategories = [];
            state.selectedSecondLevelCategories = [];
            state.selectedThirdLevelCategories = [];
            state.selectedFirstStageCategories = [];
            state.selectedSecondStageCategories = [];
            state.firstStageCategories = [];
            state.secondStageCategories = [];
            state.selectedSports = [];
            state.sportsLoading = true;
            this.getStandardSportsFromGraphQL();
        } else if (isStageEvent) {
            this.props.form.changeValue('contentCategoriesLevel1', null);
            this.props.form.changeValue('contentCategoriesLevel2', null);
            this.props.form.changeValue('contentCategoriesLevel3', null);
            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tournaments', null);
            this.props.form.changeValue('court', null);
            this.props.form.changeValue('sports', null);
            state.selectedFirstLevelCategories = [];
            state.selectedSecondLevelCategories = [];
            state.selectedThirdLevelCategories = [];
            state.selectedTournaments = [];
            state.selectedTournamentCategories = [];
            state.tournamentCategories = [];
            state.tournaments = [];
            state.stageSportsLoading = true;
            this.getStageSportsFromGraphQL();
        } else if (isNonSportradarEvent) {
            this.props.form.changeValue('sports', null);
            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tournaments', null);
            this.props.form.changeValue('court', null);
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);
            state.selectedSports = [];
            state.selectedTournaments = [];
            state.selectedTournamentCategories = [];
            state.selectedFirstStageCategories = [];
            state.selectedSecondStageCategories = [];
            state.firstStageCategories = [];
            state.secondStageCategories = [];
            state.contentCategoriesFirstLevelLoading = true;
            this.getFirstLevelContentCategoriesFromGraphQL();
        } else {
            this.props.form.changeValue('contentCategoriesLevel1', null);
            this.props.form.changeValue('contentCategoriesLevel2', null);
            this.props.form.changeValue('contentCategoriesLevel3', null);
            this.props.form.changeValue('sports', null);
            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tournaments', null);
            this.props.form.changeValue('court', null);
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);
            state = {
                ...state,
                selectedTournaments: [],
                selectedSports: [],
                selectedTournamentCategories: [],
                selectedFirstLevelCategories: [],
                selectedSecondLevelCategories: [],
                selectedThirdLevelCategories: [],
                selectedFirstStageCategories: [],
                selectedSecondStageCategories: [],
                firstStageCategories: [],
                secondStageCategories: [],
            };
        }

        this.setState(() => (state));
    };

    // load tournament categories level 1 and tv channel
    onSportChange = (event, value) => {
        if (!this.state.isSportradarEvent) return;

        let state = {
            hasSelectedSport: false,
            hasSportsWithTvChannel: false,
            tournamentCategories: [],
            tournamentCategoriesLoading: false,
            tvChannelsLoading: false,
            selectedSports: [],
        };

        if (0 < value.length) {
            const sportIds = value.map(Number);

            state = {
                hasSelectedSport: true,
                tournamentCategoriesLoading: true,
                sports: sportIds,
                selectedSports: sportIds,
            };

            setTvChannelState(this.props.form, sportIds, this.setStateHandler, MESSAGE_BOX_NAME);
            this.fetchSportradarSportDependenciesDebounced(sportIds);
        } else {
            this.fetchSportradarSportDependenciesDebounced.cancel();
            this.fetchTournamentsDebounced.cancel();

            state = {
                ...state,
                selectedTournaments: [],
                selectedTournamentCategories: [],
            };

            this.props.form.changeValue('tournamentCategories', null);
            this.props.form.changeValue('tvChannels', null);
        }

        this.setState(() => (state));
    };

    setStateHandler = (stateObject) => {
        this.setState(() => (stateObject));
    };

    onStageSportChange = (event, value) => {
        if (!this.state.isStageEvent) {
            return;
        }

        const selectedSportIds = (value || []).map(Number);

        if (_isEmpty(selectedSportIds)) {
            this.fetchFirstStageCategoriesDebounced.cancel();
            this.fetchSecondStageCategoriesDebounced.cancel();

            this.setState({
                hasSelectedSport: false,
                selectedSports: [],
                firstStageCategories: [],
                firstStageCategoriesLoading: false,
                selectedFirstStageCategories: [],
                secondStageCategories: [],
                secondStageCategoriesLoading: false,
                selectedSecondStageCategories: [],
            });
            this.props.form.changeValue('firstStageCategories', null);
            this.props.form.changeValue('secondStageCategories', null);

            return;
        }

        this.setState(
            {
                hasSelectedSport: true,
                selectedSports: selectedSportIds,
                firstStageCategoriesLoading: true,
            },
            () => {
                this.fetchSecondStageCategoriesDebounced.cancel();
                this.fetchFirstStageCategoriesDebounced(selectedSportIds);
            }
        );
    };

    onFirstStageCategoryChange = (event, value) => {
        if (_isEmpty(value)) {
            this.fetchSecondStageCategoriesDebounced.cancel();

            this.setState({
                selectedFirstStageCategories: [],
                secondStageCategories: [],
                secondStageCategoriesLoading: false,
                selectedSecondStageCategories: [],
            });
            this.props.form.changeValue('secondStageCategories', null);

            return;
        }

        const selectedFirstStageCategoryIds = value.map(Number);

        this.setState(
            {
                selectedFirstStageCategories: selectedFirstStageCategoryIds,
                secondStageCategoriesLoading: true,
            },
            () => {
                this.fetchSecondStageCategoriesDebounced(selectedFirstStageCategoryIds);
            }
        );
    };

    onSecondStageCategoryChange = (event, value) => {
        const selectedSecondStageCategoryIds = (value || []).map(Number);

        this.setState(() => ({
            selectedSecondStageCategories: selectedSecondStageCategoryIds,
        }));
    };

    // display only when at least one sport is selected
    onTournamentCategoryChange = (event, value) => {
        let state = {
            hasSelectedTournamentCategory: false,
            tournaments: [],
            tournamentsLoading: false,
        };

        if (0 < value.length) {
            state = {
                hasSelectedTournamentCategory: true,
                selectedTournamentCategories: value,
                tournamentsLoading: true,
            };

            const tournamentCategoryIds = value.map((tournamentCategory) => (convertToInt(tournamentCategory)));

            this.fetchTournamentsDebounced(tournamentCategoryIds);
        } else {
            this.fetchTournamentsDebounced.cancel();

            state = {
                ...state,
                selectedTournaments: [],
            };

            this.props.form.changeValue('tournaments', null);
        }

        this.setState(() => (state));
    };

    // load content categories level 2
    onContentCategoryLevelFirstChange = (event, value) => {
        let state = {
            hasSelectedCategoryLev1: false,
            contentCategoriesSecondLevel: [],
            contentCategoriesSecondLevelLoading: false,
        };

        if (0 < value.length) {
            state = {
                hasSelectedCategoryLev1: true,
                selectedFirstLevelCategories: value,
                contentCategoriesSecondLevelLoading: true,
            };

            const contentCategoryLevel2Ids = value.map((contentCategoryLevel2) => (
                convertToInt(contentCategoryLevel2)
            ));

            this.getSecondLevelContentCategoriesFromGraphQL(contentCategoryLevel2Ids);
        } else {
            state = {
                ...state,
                selectedSecondLevelCategories: [],
                selectedThirdLevelCategories: [],
            };

            this.props.form.changeValue('contentCategoriesLevel2', null);
        }

        this.setState(() => (state));
    };

    onContentCategorySecondLevelChange = (event, value) => {
        let state = {
            hasSelectedCategoryLev2: false,
            contentCategoriesThirdLevel: [],
            contentCategoriesThirdLevelLoading: false,
        };

        if (0 < value.length) {
            state = {
                hasSelectedCategoryLev2: true,
                selectedSecondLevelCategories: value,
                contentCategoriesThirdLevelLoading: true,
            };

            const contentCategoryLevel3Ids = value.map((contentCategoryLevel3) => (
                convertToInt(contentCategoryLevel3)
            ));

            this.getThirdLevelContentCategoriesFromGraphQL(contentCategoryLevel3Ids);
        } else {
            state = {
                ...state,
                selectedThirdLevelCategories: [],
            };

            this.props.form.changeValue('contentCategoriesLevel3', null);
        }

        this.setState(() => (state));
    };

    onContentCategoryThirdLevelChange = (event, value) => {
        this.setState(() => ({
            selectedThirdLevelCategories: value,
        }));
    };

    loadCourtsForTournaments = (sportIds, tournamentIds) => (
        this.cascadeFetchers.loadCourtsForTournaments(sportIds, tournamentIds, {
            scheduleCourtsFetch: (courtTournamentIds) => this.fetchCourtsDebounced(courtTournamentIds),
        })
    );

    onTournamentChange = (event, value) => {
        let state = {
            hasSelectedTournaments: false,
            courtsLoading: false,
            selectedTournaments: value,
        };

        if (0 < value.length) {
            this.loadCourtsForTournaments(this.state.sports, value);
        } else {
            state = {
                ...state,
                selectedCourts: [],
            };

            this.props.form.changeValue('court', null);
        }

        this.setState(() => (state));
    };

    onCourtChange = (event, value) => {
        this.setState(() => ({
            selectedCourts: value,
        }));
    };

    cascadeFetchers = createCascadeFetchers({
        getClient: () => this.props.client,
        getForm: () => this.props.form,
        getState: () => this.state,
        setState: (updater) => this.setState(updater),
        messageBoxName: MESSAGE_BOX_NAME,
    });

    fetchSportradarSportDependenciesDebounced = createGraphqlDebouncedFetch((sportIds) => {
        this.getTournamentCategoriesFromGraphQL(sportIds);
    });

    fetchTournamentsDebounced = createGraphqlDebouncedFetch((tournamentCategoriesIds) => {
        this.getTournamentsFromGraphQL(tournamentCategoriesIds);
    });

    fetchCourtsDebounced = createGraphqlDebouncedFetch((tournamentIds) => {
        this.getCourtsFromGraphQL(tournamentIds);
    });

    fetchFirstStageCategoriesDebounced = createGraphqlDebouncedFetch((selectedSportIds) => {
        this.getFirstStageCategoriesFromGraphQL(selectedSportIds);
    });

    fetchSecondStageCategoriesDebounced = createGraphqlDebouncedFetch((selectedFirstStageCategoryIds) => {
        this.getSecondStageCategoriesFromGraphQL(selectedFirstStageCategoryIds);
    });

    getStandardSportsFromGraphQL = (isStandard = true) => (
        this.cascadeFetchers.fetchStandardSports(isStandard)
    );

    getStageSportsFromGraphQL = (isStage = true) => (
        this.cascadeFetchers.fetchStageSports(isStage)
    );

    getTournamentCategoriesFromGraphQL = (sportIds) => (
        this.cascadeFetchers.fetchTournamentCategories(sportIds)
    );

    getTournamentsFromGraphQL = (tournamentCategoriesIds) => (
        this.cascadeFetchers.fetchTournaments(tournamentCategoriesIds)
    );

    getCourtsFromGraphQL = (tournamentIds) => (
        this.cascadeFetchers.fetchCourts(tournamentIds)
    );

    getFirstStageCategoriesFromGraphQL = (selectedSportIds) => (
        this.cascadeFetchers.fetchFirstStageCategories(selectedSportIds, {
            cancelPendingSecondStageFetch: () => this.fetchSecondStageCategoriesDebounced.cancel(),
            reloadSecondStageCategories: (ids) => this.getSecondStageCategoriesFromGraphQL(ids),
        })
    );

    getSecondStageCategoriesFromGraphQL = (selectedFirstStageCategoryIds) => (
        this.cascadeFetchers.fetchSecondStageCategories(selectedFirstStageCategoryIds)
    );

    getFirstLevelContentCategoriesFromGraphQL = () => (
        this.cascadeFetchers.fetchContentCategoriesLevel1()
    );

    getSecondLevelContentCategoriesFromGraphQL = createGraphqlDebouncedFetch((firstLevelIds) => {
        this.cascadeFetchers.fetchContentCategoriesLevel2(firstLevelIds);
    });

    getThirdLevelContentCategoriesFromGraphQL = createGraphqlDebouncedFetch((secondLevelIds) => {
        this.cascadeFetchers.fetchContentCategoriesLevel3(secondLevelIds);
    });

    render() {
        const propertyLicencesForDropdown = mapPropertyLicencesForDropdown(this.props.data.propertyLicencesForDropdown);
        const loading = this.props.loading;
        const {
            broadcastStatuses,
            distributionTypes,
            encodingDatacenters,
            encodingStatuses,
            eventTypes,
            tvChannels,
            products,
            eventContentTypes,
            eventContentVariants,
        } = this.props.data;

        return (
            <div className='livecontrol__filters filter__controls__container'>
                <SearchFilter
                    loading={loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <PropertyLicencesFilter
                    propertyLicences={propertyLicencesForDropdown}
                    loading={loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <EventTypeFilter
                    eventTypes={eventTypes.mapDataForDropdownWithIntVal()}
                    loading={loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                    onChangeValue={this.onEventTypeChange}
                />
                <SportsFilter
                    sports={this.state.fetchedSports}
                    loading={this.state.sportsLoading || loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                    hidden={!this.state.isSportradarEvent}
                    onChangeValue={this.state.isSportradarEvent && this.onSportChange}
                />
                <StageSportsFilter
                    stageSports={this.state.fetchedStageSports}
                    loading={this.state.stageSportsLoading || loading}
                    hidden={!this.state.isStageEvent}
                    onChangeValue={this.onStageSportChange}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <FirstStageCategoriesFilter
                    firstStageCategories={this.state.firstStageCategories}
                    loading={this.state.firstStageCategoriesLoading || loading}
                    hidden={!(this.state.isStageEvent && this.state.hasSelectedSport)}
                    onChangeValue={this.onFirstStageCategoryChange}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <SecondStageCategoriesFilter
                    secondStageCategories={this.state.secondStageCategories}
                    loading={this.state.secondStageCategoriesLoading || loading}
                    hidden={_isEmpty(this.state.selectedFirstStageCategories)}
                    filters={this.props.filters.LiveControl}
                    onChangeValue={this.onSecondStageCategoryChange}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <TournamentCategoriesFilter
                    tournamentCategories={this.state.tournamentCategories}
                    loading={this.state.tournamentCategoriesLoading || loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                    hidden={!(this.state.isSportradarEvent && this.state.hasSelectedSport)}
                    onChangeValue={this.onTournamentCategoryChange}
                />
                <TournamentsFilter
                    tournaments={this.state.tournaments}
                    loading={this.state.tournamentsLoading || loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                    hidden={!this.state.hasSelectedTournamentCategory}
                    disabled={this.state.tournamentsLoading || this.state.tournamentCategoriesLoading || loading}
                    onChangeValue={this.onTournamentChange}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='court'
                    hidden={!this.state.hasSelectedTournaments}
                    options={this.state.courts}
                    disabled={this.state.courtsLoading || loading}
                    placeholder='Court'
                    loading={this.state.courtsLoading || loading}
                    multiple
                    setValue={this.props.restoreFilterValues(this.props.filters.LiveControl, 'court')}
                    selection
                    onChangeValue={this.onCourtChange}
                    search
                />
                <ContentCategoriesLevel1Filter
                    loading={this.state.contentCategoriesFirstLevelLoading || loading}
                    hidden={!this.state.isNonSportradarEvent}
                    options={this.state.contentCategoriesFirstLevel}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                    onChangeValue={this.onContentCategoryLevelFirstChange}
                />
                <ContentCategoriesLevel2Filter
                    loading={this.state.contentCategoriesSecondLevelLoading || loading}
                    hidden={!this.state.hasSelectedCategoryLev1}
                    options={this.state.contentCategoriesSecondLevel}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                    onChangeValue={this.onContentCategorySecondLevelChange}
                />
                <ContentCategoriesLevel3Filter
                    loading={this.state.contentCategoriesThirdLevelLoading || loading}
                    hidden={!this.state.hasSelectedCategoryLev2}
                    options={this.state.contentCategoriesThirdLevel}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                    onChangeValue={this.onContentCategoryThirdLevelChange}
                />
                <ContentTypesFilter
                    eventContentTypes={eventContentTypes}
                    loading={loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <ContentVariantsFilter
                    eventContentVariants={eventContentVariants}
                    loading={loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='tvChannels'
                    hidden={!this.state.hasSportsWithTvChannel}
                    options={tvChannels.mapDataForDropdownWithIntVal()}
                    disabled={this.state.tvChannelsLoading || loading}
                    placeholder='TV channel'
                    loading={this.state.tvChannelsLoading || loading}
                    multiple
                    setValue={this.props.restoreFilterValues(this.props.filters.LiveControl, 'tvChannels')}
                    selection
                    search
                />
                <Field
                    className='--customWidth --encodingDatacenter'
                    component={Form.ReduxSelect}
                    name='encodingDatacenter'
                    placeholder='Datacenter'
                    options={[].concat(encodingDatacenters.mapDataForDropdownWithIntVal()).sort((a, b) => (
                        a.value > b.value ? 1 : -1
                    ))}
                    search
                    loading={loading}
                    multiple
                    disabled={loading}
                    setValue={this.props.restoreFilterValues(this.props.filters.LiveControl, 'encodingDatacenter')}
                />
                <ProductsFilter
                    products={products.mapDataForDropdownWithIntVal()}
                    loading={loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <DistributionTypesFilter
                    distributionTypes={distributionTypes.mapDataForDropdownWithIntVal()}
                    loading={loading}
                    filters={this.props.filters.LiveControl}
                    restoreFilterValues={this.props.restoreFilterValues}
                />
                <Field
                    className='--customWidth --encodingStatus'
                    component={Form.ReduxSelect}
                    name='encodingStatus'
                    placeholder='Encoding status'
                    options={[{value: null, key: 7, text: 'Not started'}]
                        .concat(encodingStatuses.mapDataForDropdownWithIntVal())
                        .sort(sortByKey('text'))}
                    search
                    loading={loading}
                    multiple
                    disabled={loading}
                    setValue={this.props.restoreFilterValues(this.props.filters.LiveControl, 'encodingStatus')}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='startBroadcastAutomatically'
                    className='--customWidth --startBroadcastAutomatically'
                    placeholder='Broadcast'
                    loading={loading}
                    disabled={loading}
                    options={[
                        {
                            value: 1,
                            key: 1,
                            text: 'Start broadcast automatically',
                        },
                        {
                            value: 0,
                            key: 0,
                            text: 'Start broadcast manually',
                        },
                    ]}
                    setValue={this.props.restoreFilterValues(this.props.filters.LiveControl, 'startBroadcastAutomatically')}
                />
                <Field
                    className='--customWidth --broadcastStatus'
                    component={Form.ReduxSelect}
                    name='broadcastStatus'
                    placeholder='Broadcast status'
                    options={orderBy(broadcastStatuses.mapDataForDropdownWithIntVal(), ['text'], ['asc'])}
                    search
                    loading={loading}
                    multiple
                    disabled={loading}
                    setValue={this.props.restoreFilterValues(this.props.filters.LiveControl, 'broadcastStatus')}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='preBuffer'
                    className='preBuffer'
                    placeholder='Select pre buffer'
                    loading={loading}
                    disabled={loading}
                    options={getBufferDropdownOptions('-')}
                    setValue={this.props.restoreFilterValues(this.props.filters.LiveControl, 'preBuffer', 2)}
                    allowClear={false}
                />
                <Field
                    component={Form.ReduxSelect}
                    name='postBuffer'
                    className='postBuffer'
                    placeholder='Select post buffer'
                    loading={loading}
                    disabled={loading}
                    options={getBufferDropdownOptions('+')}
                    setValue={this.props.restoreFilterValues(this.props.filters.LiveControl, 'postBuffer', 2)}
                    allowClear={false}
                />
            </div>
        );
    }
}

export default FiltersWrapper(
    withApollo(
        connect(null, mapModulesToProps(['MessageBox']))(LiveControlFilters)
    ), {
        name: 'LiveControl',
        initialValues: {
            preBuffer: 2,
            postBuffer: 2,
        },
    }
);
